import DOMPurify from 'dompurify'
import ReactMarkdown from 'react-markdown'
import type {
  INotebookContent,
  ICell,
  ICodeCell,
  IMarkdownCell,
  IOutput,
  IStream,
  IExecuteResult,
  IDisplayData,
  IError,
} from '@jupyterlab/nbformat'
import './PythonNotebook.css'

interface ReadOnlyPythonNotebookProps {
  notebook: INotebookContent | undefined
}

// eslint-disable-next-line no-control-regex -- ANSI ESC (0x1b) is the character we need to match
const ANSI_REGEX = /\x1b\[[0-9;]*[A-Za-z]/g

function joinMultiline(value: string | string[] | undefined): string {
  if (value == null) return ''
  return Array.isArray(value) ? value.join('') : value
}

function stripAnsi(text: string): string {
  return text.replace(ANSI_REGEX, '')
}

function OutputView({ output }: { output: IOutput }) {
  if (output.output_type === 'stream') {
    const stream = output as IStream
    const isStderr = stream.name === 'stderr'
    return (
      <pre
        className={`jp-OutputArea-output whitespace-pre-wrap text-sm font-mono ${
          isStderr ? 'text-destructive' : ''
        }`}
        data-mime-type={isStderr ? 'application/vnd.jupyter.stderr' : undefined}
      >
        {joinMultiline(stream.text)}
      </pre>
    )
  }

  if (output.output_type === 'execute_result' || output.output_type === 'display_data') {
    const data = (output as IExecuteResult | IDisplayData).data || {}

    if (data['image/png']) {
      return (
        <img
          alt="output"
          src={`data:image/png;base64,${joinMultiline(data['image/png'] as string | string[])}`}
          className="max-w-full"
        />
      )
    }
    if (data['image/jpeg']) {
      return (
        <img
          alt="output"
          src={`data:image/jpeg;base64,${joinMultiline(data['image/jpeg'] as string | string[])}`}
          className="max-w-full"
        />
      )
    }
    if (data['image/svg+xml']) {
      return (
        <div
          className="jp-RenderedHTMLCommon"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              joinMultiline(data['image/svg+xml'] as string | string[]),
            ),
          }}
        />
      )
    }
    if (data['text/html']) {
      return (
        <div
          className="jp-RenderedHTMLCommon overflow-x-auto"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(joinMultiline(data['text/html'] as string | string[])),
          }}
        />
      )
    }
    if (data['text/markdown']) {
      return (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown>{joinMultiline(data['text/markdown'] as string | string[])}</ReactMarkdown>
        </div>
      )
    }
    if (data['text/plain']) {
      return (
        <pre className="jp-OutputArea-output whitespace-pre-wrap text-sm font-mono">
          {joinMultiline(data['text/plain'] as string | string[])}
        </pre>
      )
    }
    return null
  }

  if (output.output_type === 'error') {
    const err = output as IError
    const traceback = (err.traceback || []).map(stripAnsi).join('\n')
    return (
      <pre className="jp-OutputArea-output whitespace-pre-wrap text-sm font-mono bg-destructive/10 rounded p-2 text-destructive">
        {traceback || `${err.ename}: ${err.evalue}`}
      </pre>
    )
  }

  return null
}

function CodeCellView({ cell }: { cell: ICodeCell }) {
  const source = joinMultiline(cell.source)
  const executionCount = cell.execution_count
  const promptLabel = executionCount != null ? `[${executionCount}]` : '[ ]'

  return (
    <div className="jp-Cell jp-CodeCell flex flex-col gap-1 py-2">
      <div className="flex gap-3 items-start">
        <div className="jp-InputPrompt shrink-0 select-none">In {promptLabel}:</div>
        <pre className="jp-InputArea-editor flex-1 min-w-0 overflow-x-auto bg-card border border-border rounded text-sm font-mono px-3 py-2 whitespace-pre">
          {source}
        </pre>
      </div>
      {cell.outputs && cell.outputs.length > 0 && (
        <div className="flex gap-3 items-start">
          <div className="jp-OutputPrompt shrink-0 select-none">Out {promptLabel}:</div>
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            {cell.outputs.map((output, i) => (
              <OutputView key={i} output={output} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function MarkdownCellView({ cell }: { cell: IMarkdownCell }) {
  return (
    <div className="jp-Cell jp-MarkdownCell py-2 ps-[68px] pe-2">
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown>{joinMultiline(cell.source)}</ReactMarkdown>
      </div>
    </div>
  )
}

function NotebookStaticView({ nbformat }: { nbformat: INotebookContent }) {
  return (
    <div className="flex flex-col gap-2 p-3">
      {nbformat.cells.map((cell: ICell, i: number) => {
        if (cell.cell_type === 'code') {
          return <CodeCellView key={i} cell={cell as ICodeCell} />
        }
        if (cell.cell_type === 'markdown') {
          return <MarkdownCellView key={i} cell={cell as IMarkdownCell} />
        }
        return null
      })}
    </div>
  )
}

export const ReadOnlyPythonNotebook = ({ notebook }: ReadOnlyPythonNotebookProps) => {
  if (!notebook?.cells) return null

  return (
    <div
      className="notebook-wrapper relative h-full w-full flex flex-col overflow-hidden"
      dir="ltr"
    >
      <div className="flex-1 min-h-0 overflow-auto">
        <NotebookStaticView nbformat={notebook} />
      </div>
    </div>
  )
}
